import React, { useCallback, useContext, useMemo } from 'react';
import slugify from 'slugify';
import { type ViewState, itemTypeToUIMap } from '../types';
import { metric } from '../util';
import { ClickableElement, FocusableList, FocusableListProps } from '@/atoms';
import { entriesOf, keysOf, useId } from '@/functions';
import { colors } from '@/styles';
import { RadioSquareSelect, RadioSquareUnselect } from '@/svgs';
import { MetroContext } from '@/classes';

export type FilterListProps = Partial<FocusableListProps> & {
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
};

export function FilterList({ viewState, setViewState, ...props }: FilterListProps) {
  const { filterButtonActive, selectedItemTypes } = viewState;

  const ids = useId(Object.keys(itemTypeToUIMap).length + 1, 'livefire-filter-');
  const metroContext = useContext(MetroContext);
  const onEscapePressed = useCallback(() => {
    setViewState((state) => ({
      ...state,
      filterButtonActive: false,
    }));
    return true;
  }, [setViewState]);

  const allSelected = useMemo(
    () =>
      Boolean(
        Object.keys(selectedItemTypes).length &&
          Object.values(selectedItemTypes).every((value) => value),
      ),
    [selectedItemTypes],
  );

  if (!filterButtonActive) {
    return null;
  }

  const listItems = entriesOf(itemTypeToUIMap).map(([itemType, displayText]) => {
    const selected = !!selectedItemTypes[itemType];
    const id = ids.shift();

    return (
      <ClickableElement
        key={itemType}
        tagName="div"
        className="livefire-filter-item"
        aria-selected={selected}
        role="option"
        id={id}
        onClick={() => {
          setViewState((state) => ({
            ...state,
            selectedItemTypes: {
              ...state.selectedItemTypes,
              [itemType]: !selected,
            },
          }));

          metric(
            `click.livefire.filter.${slugify(itemType, { replacement: '_', lower: true })}.${
              !selected ? 'on' : 'off'
            }`,
            metroContext,
          )();
        }}
      >
        {selected ? (
          <RadioSquareSelect title="Selected" size={`${12 / 2.85}`} fill={colors.taosSky} />
        ) : (
          <RadioSquareUnselect title="Not selected" size={`${12 / 2.85}`} />
        )}
        {displayText}
      </ClickableElement>
    );
  });

  return (
    <FocusableList className="livefire-filter-list" onEscapePressed={onEscapePressed} {...props}>
      {listItems}
      <ClickableElement
        tagName="div"
        className="livefire-filter-item"
        aria-selected={allSelected}
        role="option"
        id={ids.shift()}
        onClick={() => {
          setViewState((state) => ({
            ...state,
            selectedItemTypes: keysOf(selectedItemTypes).reduce((obj, key) => {
              obj[key] = !allSelected;
              return obj;
            }, {} as ViewState['selectedItemTypes']),
          }));

          metric(`click.livefire.filter.all.${!allSelected ? 'on' : 'off'}`, metroContext)();
        }}
      >
        {allSelected ? (
          <RadioSquareSelect title="Selected" fill={colors.taosSky} size={`${12 / 2.85}`} />
        ) : (
          <RadioSquareUnselect title="Not selected" size={`${12 / 2.85}`} />
        )}
        All
      </ClickableElement>
    </FocusableList>
  );
}
